'use client'

import { useEffect, useRef } from 'react'

import { geoBeanchLocation } from '@/libs/haversine'

import { useDestination } from '../../../destination/context/DestinationContext'
import type { IBranchOption } from '../types/schema'

interface Args {
  /**
   * Branches this gift can be redeemed at, already product-scoped by Laravel
   * (`getBranchList(.., pd_id)`). Also the allow-list — never auto-select outside it.
   */
  branches: IBranchOption[]
  /** False once the visitor has a branch — from a `?branch=` prefill or their own choice. */
  enabled: boolean
  onPick: (branchId: number) => void
}

/**
 * Pre-selects the nearest branch, as `booking.tsx` and `promotionBookingForm.tsx` already do.
 *
 * Joins two sources: `/api/egift/filter` has the redeemable branches but no coordinates,
 * the branch list SSR'd into `DestinationProvider` has coordinates but knows nothing about the
 * gift. Matched on
 * `String(branch_id) === Branch.id` — both read `bl_branchinfo`. Empty intersection = cannot tell,
 * so do nothing. Runs once, and never overwrites a branch chosen while the lookup was in flight.
 */
export function useNearestBranch({ branches, enabled, onPick }: Args) {
  // Already SSR'd by the layout — refetching it would be a second trip for data we hold.
  const { data } = useDestination()
  const grouped: IBranch[] = data?.branches ?? []

  const ran = useRef(false)

  // Refs, so a re-render cannot restart the lookup or fire a stale callback.
  const enabledRef = useRef(enabled)
  const onPickRef = useRef(onPick)
  enabledRef.current = enabled
  onPickRef.current = onPick

  useEffect(() => {
    if (!enabled || ran.current || !branches.length || !grouped.length) return
    ran.current = true

    let cancelled = false

    async function selectNearest() {
      try {
        const redeemable = new Set(branches.map((branch) => String(branch.branch_id)))
        const candidates = grouped
          .flatMap((city) => city.branches)
          .filter((branch) => redeemable.has(branch.id))

        // No overlap = the id spaces disagree, or the gift is limited to branches the public list
        // omits. Either way, nothing to be confident about.
        if (!candidates.length) return

        // Resolves null past 50 km, and rejects when the visitor declines the prompt.
        const nearest = await geoBeanchLocation(candidates)
        if (cancelled || !nearest) return

        // The user may have picked a branch while the browser was asking for permission.
        if (!enabledRef.current) return

        const branchId = Number(nearest.id)
        if (Number.isFinite(branchId)) onPickRef.current(branchId)
      } catch {
        // Denied or unsupported. An unpicked location is this field's normal state — never
        // surface it.
      }
    }

    void selectNearest()

    return () => {
      cancelled = true
    }
  }, [enabled, branches, grouped])
}
